import React, { useState } from "react";
import {
  FaTimes,
  FaPaperclip,
  FaTrash,
  FaFilePdf,
  FaFileImage,
  FaFileAlt,
} from "react-icons/fa";
import { FiChevronDown } from "react-icons/fi";
import { message } from "antd";
import dayjs from "dayjs";
import ClinicalNotesService from "../../../../Services/ClinicalNotesService";

function AddNoteModal({
  onClose,
  onSuccess,
  patientDetails,
  patientId,
  admissionId,
}) {
  const loginObj =
    sessionStorage.getItem("login_result") ||
    localStorage.getItem("login_result");
  const userData = loginObj ? JSON.parse(loginObj) : null;
  const activePatient = JSON.parse(sessionStorage.getItem("patient") || "null");

  const effectivePatientId =
    patientId || patientDetails?.patientId || activePatient?.patientId || "";
  const effectiveAdmissionNo =
    patientDetails?.admissionNo ||
    activePatient?.admissionNo ||
    admissionId ||
    "";
  const effectiveEncounterId =
    patientDetails?.encounterId ||
    patientDetails?.admissionId ||
    activePatient?.admissionId ||
    admissionId ||
    0;

  const currentUserId = userData?.user?.userId || "DZU7321276";
  const currentUserName =
    userData?.user?.userName || userData?.user?.firstName || "Dr. Hari Prakash";

  const [isLoading, setIsLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [formData, setFormData] = useState({
    noteTypeId: 0,
    noteDatetime: dayjs().format("YYYY-MM-DD HH:mm:ss"),
    noteText: "",
    nurseActionRequired: "N",
    priority: "Medium",
    recordedBy: currentUserId,
    recordedByName: currentUserName,
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    files.forEach((file) => {
      if (file.size > 10 * 1024 * 1024) {
        message.warning(`${file.name} exceeds 10MB limit.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (uploadEvent) => {
        const fullDataUrl = uploadEvent.target.result;
        const base64Pure = fullDataUrl.includes(",")
          ? fullDataUrl.split(",")[1]
          : fullDataUrl;
        const extension = file.name.split(".").pop().toLowerCase();

        const newAttachment = {
          fileName: file.name,
          fileSizeKb: Math.round(file.size / 1024),
          entityType: "CLINICAL_NOTE",
          image: base64Pure,
          fileType: extension || "pdf",
          isDeleted: "N",
        };

        setAttachments((prev) => [...prev, newAttachment]);
      };
      reader.readAsDataURL(file);
    });

    e.target.value = "";
  };

  const handleRemoveAttachment = (indexToRemove) => {
    setAttachments((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const getFileIcon = (type) => {
    if (type === "pdf") return <FaFilePdf className="text-red-500 text-sm" />;
    if (["jpg", "jpeg", "png", "webp"].includes(type))
      return <FaFileImage className="text-blue-500 text-sm" />;
    return <FaFileAlt className="text-gray-500 text-sm" />;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    if (!formData.noteText || !formData.noteText.trim()) {
      message.warning("Please enter note content before saving.");
      return;
    }

    setIsLoading(true);
    const currentFormattedDate = dayjs().format("YYYY-MM-DD HH:mm:ss");

    const payload = {
      admissionNo: String(effectiveAdmissionNo),
      encounterId: Number(effectiveEncounterId) || 0,
      patientId: String(effectivePatientId),
      noteTypeId: Number(formData.noteTypeId) || 0,
      attachments: attachments.map((att) => ({
        fileSizeKb: att.fileSizeKb,
        entityType: att.entityType || "CLINICAL_NOTE",
        image: att.image,
        fileType: att.fileType,
        isDeleted: "N",
      })),
      noteText: formData.noteText.trim(),
      noteDatetime: formData.noteDatetime || currentFormattedDate,
      nurseActionRequired: formData.nurseActionRequired || "N",
      priority: formData.priority || "Medium",
      recordedBy: formData.recordedBy || currentUserId,
      recordedByName: formData.recordedByName || currentUserName,
      createdBy: currentUserId,
      createdDatetime: currentFormattedDate,
      updatedBy: currentUserId,
      updatedDatetime: currentFormattedDate,
      isDeleted: "N",
    };

    try {
      await ClinicalNotesService.saveClinicalNote(payload);
      message.success("Clinical Note saved successfully.");
      if (onSuccess) {
        onSuccess();
      }
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error("Error saving clinical note:", error);
      message.error(
        error?.response?.data?.message ||
          "Failed to save clinical note. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-white rounded-[22px] overflow-hidden shadow-xl max-h-[95vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 flex items-center justify-between border-b border-[#edf1f7]">
          <div>
            <h2 className="text-[18px] font-bold text-[#111827]">
              Add Clinical Note
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-[#6b7280] bg-white hover:text-[#111827]"
          >
            <FaTimes className="text-[16px]" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] font-semibold text-[#374151] mb-2">
                Date & Time
              </label>
              <input
                type="datetime-local"
                value={dayjs(formData.noteDatetime).format("YYYY-MM-DDTHH:mm")}
                onChange={(e) => {
                  const val = e.target.value
                    ? dayjs(e.target.value).format("YYYY-MM-DD HH:mm:ss")
                    : dayjs().format("YYYY-MM-DD HH:mm:ss");
                  handleInputChange("noteDatetime", val);
                }}
                className="w-full h-10 px-3 border border-[#edf1f7] rounded-lg text-[13px] text-[#111827] outline-none focus:border-[#008f9f] bg-white"
              />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-[#374151] mb-2">
                Priority
              </label>
              <div className="relative">
                <select
                  value={formData.priority}
                  onChange={(e) =>
                    handleInputChange("priority", e.target.value)
                  }
                  className="w-full h-10 px-3 border border-[#edf1f7] rounded-lg text-[13px] text-[#111827] outline-none appearance-none focus:border-[#008f9f] bg-white"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
                <FiChevronDown className="absolute right-3 top-3 text-[#6b7280] pointer-events-none" />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-[12px] font-semibold text-[#374151] mb-2">
              Note Content
            </label>
            <div className="border border-[#edf1f7] rounded-lg overflow-hidden">
              <textarea
                rows="5"
                value={formData.noteText}
                onChange={(e) => handleInputChange("noteText", e.target.value)}
                placeholder="Enter patient review, clinical observation, treatment response..."
                className="w-full p-4 text-[13px] text-[#111827] outline-none resize-none focus:ring-1 focus:ring-[#008f9f]"
              ></textarea>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-[12px] font-semibold text-[#374151]">
                Attachments ({attachments.length})
              </label>
              <span className="text-[11px] text-[#9ca3af]">
                PDF, JPG, PNG up to 10MB
              </span>
            </div>

            <label className="border-2 border-dashed border-[#edf1f7] rounded-xl bg-[#fafbff] flex flex-col items-center justify-center py-5 cursor-pointer hover:bg-gray-50 transition-colors block">
              <input
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                className="hidden"
                onChange={handleFileUpload}
              />
              <div className="flex items-center justify-center bg-blue-50 w-9 h-9 rounded-full mb-2 mx-auto">
                <FaPaperclip className="text-[#5b5bd6]" />
              </div>
              <p className="text-[12px] font-medium text-[#4b5563] text-center">
                <span className="text-[#5b5bd6] font-bold">
                  Click to upload
                </span>
              </p>
            </label>

            {attachments.length > 0 && (
              <div className="mt-2.5 space-y-1.5">
                {attachments.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2.5 rounded-lg border border-[#edf1f7] bg-white text-xs"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {getFileIcon(file.fileType)}
                      <span className="font-semibold text-gray-800 truncate">
                        {file.fileName ||
                          `Attachment_${index + 1}.${file.fileType}`}
                      </span>
                      <span className="text-gray-400 text-[11px]">
                        ({file.fileSizeKb} KB)
                      </span>
                    </div>
                    <button
                      onClick={() => handleRemoveAttachment(index)}
                      className="text-gray-400 hover:text-red-500 bg-white cursor-pointer p-1"
                    >
                      <FaTrash className="text-xs" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#edf1f7] flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-lg bg-white border border-[#edf1f7] text-[#4b5563] text-[13px] font-bold hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            disabled={isLoading}
            onClick={handleSubmit}
            className="px-6 py-2 rounded-lg bg-[#008f9f] text-white text-[13px] font-bold hover:bg-[#007a8a] disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {isLoading ? "Saving..." : "Save Note"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddNoteModal;
